import apiClient, { API_BASE_URL } from './apiClient';
import {
  DiagnosisResponse,
  PatientInfoPayload,
  CommunityKnowledgeResponse,
  DiagnosisSessionResponse,
} from '../types';

// 定义Message类型
export interface Message {
  role: string;
  content: string;
  timestamp?: string;
  image_url?: string;  // 添加图片URL字段
  isTemporary?: boolean; // 标记是否为临时消息
  message_type?: string;
  metadata?: Record<string, unknown>;
}

export { API_BASE_URL };

export interface DiagnosisSessionStartPayload {
  sessionId?: string | null;
  facts?: Record<string, unknown>;
  forceReset?: boolean;
  restore?: boolean;
}

export interface DiagnosisSessionUpdatePayload {
  sessionId: string;
  facts?: Record<string, unknown>;
  advance?: boolean;
  manualOverride?: boolean;
}

export async function startDiagnosisSession(
  payload: DiagnosisSessionStartPayload = {},
): Promise<DiagnosisSessionResponse> {
  const response = await apiClient.post<DiagnosisSessionResponse>('/diagnosis/session/start', {
    session_id: payload.sessionId ?? undefined,
    facts: payload.facts ?? {},
    force_reset: payload.forceReset ?? false,
    restore: payload.restore ?? true,
  });
  return response.data;
}

export async function updateDiagnosisSession(
  payload: DiagnosisSessionUpdatePayload,
): Promise<DiagnosisSessionResponse> {
  const response = await apiClient.post<DiagnosisSessionResponse>('/diagnosis/session/update', {
    session_id: payload.sessionId,
    facts: payload.facts ?? {},
    advance: payload.advance ?? false,
    manual_override: payload.manualOverride ?? false,
  });
  return response.data;
}

export async function fetchDiagnosisSession(
  sessionId: string,
  options: { restore?: boolean } = {},
): Promise<DiagnosisSessionResponse> {
  const params = new URLSearchParams();
  if (typeof options.restore === 'boolean') {
    params.set('restore', String(options.restore));
  }
  const querySuffix = params.toString();
  const url = `/diagnosis/session/${encodeURIComponent(sessionId)}${querySuffix ? `?${querySuffix}` : ''}`;
  const response = await apiClient.get<DiagnosisSessionResponse>(url);
  return response.data;
}

// 定义请求类型
export interface ChatRequest {
  message: string;
  chat_history?: any[];
}

// 定义多模态请求类型
export interface MultiModalChatRequest {
  message: string;
  chat_history?: any[];
  image_data?: string;  // Base64编码的图片数据
}

// 文生图请求类型
export interface TextToImageRequest {
  prompt: string;
  negative_prompt?: string;
  n?: number;
  size?: string;
}

// 文生图响应类型
export interface TextToImageResponse {
  image_urls: string[];
  conversation_id: string;
}

// 中医诊断请求参数
interface DiagnosisRequestPayload {
  message: string;
  patient_info?: PatientInfoPayload;
}

// 发送诊断消息
export async function sendDiagnosisMessage(
  message: string,
  options: {
    conversationId?: string;
    patientInfo?: PatientInfoPayload;
  } = {}
): Promise<DiagnosisResponse> {
  const params = new URLSearchParams();
  if (options.conversationId) {
    params.set('conversation_id', options.conversationId);
  }

  const urlParams = params.toString();
  const url = `/tcm/diagnosis${urlParams ? `?${urlParams}` : ''}`;

  const payload: DiagnosisRequestPayload = {
    message,
  };

  if (options.patientInfo) {
    payload.patient_info = options.patientInfo;
  }

  const response = await apiClient.post<DiagnosisResponse>(url, payload);
  return response.data;
}

export interface CommunityKnowledgeQueryOptions {
  limitPosts?: number;
  limitComments?: number;
  includeComments?: boolean;
}

export async function fetchCommunityKnowledge(
  query: string,
  options: CommunityKnowledgeQueryOptions = {}
): Promise<CommunityKnowledgeResponse> {
  const params = new URLSearchParams({ query });

  if (typeof options.limitPosts === 'number') {
    params.set('limit_posts', String(options.limitPosts));
  }

  if (typeof options.limitComments === 'number') {
    params.set('limit_comments', String(options.limitComments));
  }

  if (typeof options.includeComments === 'boolean') {
    params.set('include_comments', String(options.includeComments));
  }

  const url = `/assistant/community-knowledge?${params.toString()}`;
  const response = await apiClient.get<CommunityKnowledgeResponse>(url);
  return response.data;
}

// 发送普通消息（非流式）
export async function sendMessage(
  message: string,
  conversationId?: string,
  chatHistory?: any[],
  useStream: boolean = false,
  onTokenReceived?: (token: string) => void,
  onImageReceived?: (imageUrl: string) => void
): Promise<{ content: string, conversationId: string, image_url?: string }> {
  console.log(`发送消息: '${message}', conversationId: ${conversationId || '新会话'}, 历史消息数: ${chatHistory?.length || 0}, 使用流式响应: ${useStream}`);

  if (useStream) {
    return sendStreamMessage(message, conversationId, chatHistory, onTokenReceived, onImageReceived);
  }

  try {
    // 构建请求对象
    const request: ChatRequest = {
      message,
      chat_history: chatHistory || []
    };

    // 设置请求头
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // 如果有会话ID，添加到URL参数
    let url = `/chat`;
    if (conversationId) {
      url += `?conversation_id=${conversationId}`;
    }

    console.log(`发送POST请求到: ${url}`);

    // 发送请求
  const response = await apiClient.post(url, request, { headers });

    // 构建返回对象
    const result: {
      content: string,
      conversationId: string,
      image_url?: string
    } = {
      content: response.data.response,
      conversationId: response.data.conversation_id
    };

    // 如果响应中包含图片URL
    if (response.data.image_url) {
      result.image_url = response.data.image_url;
    }

    return result;
  } catch (error) {
    console.error(`发送消息失败:`, error);
    throw error;
  }
}

// 使用表单发送多模态消息（图片+文本）
export async function sendMultiModalMessage(
  message: string,
  imageFile: File,
  conversationId?: string,
  chatHistory?: any[]
) {
  try {
    console.log(`发送多模态消息: '${message}', 图片: ${imageFile.name}, conversationId: ${conversationId || '新会话'}, 历史消息数: ${chatHistory?.length || 0}`);
    
    // 构建FormData
    const formData = new FormData();
    formData.append('message', message);
    formData.append('file', imageFile);
    
    // 如果有会话ID，添加到URL参数
    let url = `/chat/multimodal`;
    if (conversationId) {
      url += `?conversation_id=${conversationId}`;
    }
    
    console.log(`发送多模态请求到: ${url}`);
    
    // 发送请求
    const response = await apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    
    console.log('多模态响应:', response.data);
    
    return {
      content: response.data.response,
      conversationId: response.data.conversation_id
    };
  } catch (error) {
    console.error(`发送多模态消息失败:`, error);
    throw error;
  }
}

// 使用JSON发送多模态消息（Base64图片+文本）
export async function sendMultiModalJsonMessage(
  message: string,
  imageData: string,
  conversationId?: string,
  chatHistory?: any[]
): Promise<{ content: string, conversationId: string, image_url?: string }> {
  try {
    console.log(`发送多模态JSON消息: '${message}', 图片数据长度: ${imageData.length}, conversationId: ${conversationId || '新会话'}, 历史消息数: ${chatHistory?.length || 0}`);
    
    // 确保图片 base64 数据格式正确
    let processedImageData = imageData;
    if (imageData.startsWith('data:')) {
      // 已经是正确格式，保持不变
      console.log('图片数据已包含 data URI 前缀');
    } else {
      // 添加 data URI 前缀
      processedImageData = `data:image/jpeg;base64,${imageData}`;
      console.log('已添加 data URI 前缀到图片数据');
    }
    
    // 确保聊天历史中每个消息对象都有role和content字段
    const sanitizedChatHistory = chatHistory ? chatHistory.map(msg => {
      // 确保消息有必要的字段
      if (typeof msg === 'object' && msg !== null) {
        return {
          role: msg.role || 'user',
          content: msg.content || '',
          timestamp: msg.timestamp || new Date().toISOString()
        };
      }
      // 跳过无效消息
      console.warn('跳过无效历史消息', msg);
      return null;
    }).filter(Boolean) : [];
    
    // 构建请求对象
    const request: MultiModalChatRequest = {
      message,
      chat_history: sanitizedChatHistory,
      image_data: processedImageData
    };
    
    // 如果有会话ID，添加到URL参数
    let url = `/chat/multimodal-json`;
    if (conversationId) {
      url += `?conversation_id=${conversationId}`;
    }
    
    console.log(`发送多模态JSON请求到: ${url}`);
    console.log(`请求历史消息数: ${sanitizedChatHistory.length}`);
    
    // 发送请求
    const response = await apiClient.post(url, request, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    console.log('多模态JSON响应:', response.data);
    
    // 确保响应内容是字符串
    let content = '';
    if (response.data && response.data.response) {
      if (typeof response.data.response === 'string') {
        content = response.data.response;
      } else {
        // 如果响应不是字符串，尝试转换
        try {
          content = JSON.stringify(response.data.response);
        } catch (err) {
          console.error('响应内容转换失败:', err);
          content = '收到响应，但格式无法处理。';
        }
      }
    } else {
      content = '未收到有效响应。';
    }
    
    // 构建返回对象
    const result: { 
      content: string, 
      conversationId: string,
      image_url?: string 
    } = {
      content: content,
      conversationId: response.data.conversation_id || conversationId || ''
    };
    
    // 如果响应中包含图片URL
    if (response.data.image_url) {
      result.image_url = response.data.image_url;
    }
    
    return result;
  } catch (error) {
    console.error(`发送多模态JSON消息失败:`, error);
    throw error;
  }
}

// 发送流式消息
export async function sendStreamMessage(
  message: string,
  conversationId?: string,
  chatHistory?: any[],
  onTokenReceived?: (token: string) => void,
  onImageReceived?: (imageUrl: string) => void
): Promise<{ content: string, conversationId: string, image_url?: string }> {
  return new Promise((resolve, reject) => {
    try {
      console.log(`发送流式消息: '${message}', conversationId: ${conversationId || '新会话'}, 历史消息数: ${chatHistory?.length || 0}`);

      // 记录收到的内容
      let receivedContent = '';
      let receivedConversationId = conversationId || '';

      // 构建事件源URL，包含消息和会话ID
      const params = new URLSearchParams();
      params.append('message', message);
      if (chatHistory && chatHistory.length > 0) {
        // 将聊天历史JSON序列化并进行URL编码
        params.append('chat_history', encodeURIComponent(JSON.stringify(chatHistory)));
      }
      if (conversationId) {
        params.append('conversation_id', conversationId);
      }

      let eventSourceUrl = `${API_BASE_URL}/chat/stream?${params.toString()}`;

      console.log(`建立EventSource连接: ${eventSourceUrl}`);

      // 创建EventSource用于接收流式响应
      const eventSource = new EventSource(eventSourceUrl);

      eventSource.onopen = () => {
        console.log('EventSource连接已打开。');
      };

      // 处理接收到的消息
      eventSource.onmessage = (event: MessageEvent) => {
        try {
          console.log(`收到消息事件 (长度:${event.data?.length || 0}):`, event.data);

          // 检查事件数据是否为空
          if (!event.data) {
            console.warn('收到空消息事件，跳过处理');
            return;
          }

          // 字符串数据直接作为token
          const token = event.data;
          receivedContent += token;

          // 调用回调函数更新UI
          if (onTokenReceived) {
            console.log(`调用回调函数更新UI - token: ${token}`);
            onTokenReceived(token);
          }
        } catch (error) {
          console.error('处理消息事件失败:', error);
        }
      };

      // 处理完成事件
      eventSource.addEventListener('done', (event: Event) => {
        try {
          const messageEvent = event as MessageEvent;
          console.log('收到完成事件:', messageEvent.data);

          // 尝试解析数据
          let data: { conversation_id?: string; image_url?: string } = {};
          try {
            data = JSON.parse(messageEvent.data);
            console.log('解析完成事件数据成功:', data);
          } catch (parseError) {
            console.warn('完成事件JSON解析失败，使用空对象:', parseError);
            // 尝试记录原始数据以便调试
            console.log('原始数据:', messageEvent.data);
          }

          if (data && data.conversation_id) {
            receivedConversationId = data.conversation_id;
            console.log(`获取到会话ID: ${receivedConversationId}`);
          }

          console.log(`流式响应完成，会话ID: ${receivedConversationId}, 内容长度: ${receivedContent.length}`);

          // 确保EventSource连接关闭，防止内存泄漏
          eventSource.close();

          // 如果有图片回调且data中包含image_url，则调用onImageReceived
          if (onImageReceived && data.image_url) {
            onImageReceived(data.image_url);
          }

          // 返回结果
          resolve({
            content: receivedContent,
            conversationId: receivedConversationId,
            image_url: data.image_url
          });
          console.log('流式响应处理完成，Promise已解析');
        } catch (error) {
          console.error('处理完成事件失败:', error);
          eventSource.close();
          reject(error);
        }
      });

      // 处理错误事件
      eventSource.onerror = (event: Event) => {
        console.error('EventSource错误:', event);
        eventSource.close();
        reject(new Error('流式响应连接出错或中断。'));
      };

    } catch (error) {
      console.error('初始化流式请求失败:', error);
      reject(error);
    }
  });
}

// 获取对话历史
export const getChatHistory = async (conversationId: string) => {
  const response = await apiClient.get(`/history/${conversationId}`);
  return response.data;
};

// 文生图API调用
export async function callTextToImage(
  prompt: string,
  options: {
    negative_prompt?: string;
    n?: number;
    size?: string;
    conversationId?: string;
  } = {}
): Promise<TextToImageResponse> {
  try {
    console.log(`发送文生图请求 - 提示词: '${prompt}', 会话ID: ${options.conversationId || '新会话'}`);
    
    // 构建请求对象
    const request: TextToImageRequest = {
      prompt: prompt
    };
    
    // 添加可选参数
    if (options.negative_prompt) request.negative_prompt = options.negative_prompt;
    if (options.n) request.n = options.n;
    if (options.size) request.size = options.size;
    
    // 设置请求头
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // 如果有会话ID，添加到URL参数
    let url = `/text2image`;
    if (options.conversationId) {
      url += `?conversation_id=${options.conversationId}`;
    }
    
    console.log(`发送文生图POST请求到: ${url}`);
    
    // 发送请求
  const response = await apiClient.post(url, request, { headers });
    
    console.log('文生图响应:', response.data);
    
    // 返回结果
    return {
      image_urls: response.data.image_urls,
      conversation_id: response.data.conversation_id
    };
  } catch (error) {
    console.error(`文生图请求失败:`, error);
    throw error;
  }
} 